import { useContext, useEffect, useRef, useState } from 'react';
import VerifiedIcon from '@mui/icons-material/Verified';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';

import ProviderDetails from './ProviderInfo';
import ChildNameHeading from 'components/SiblingSwitch/ChildNameHeading';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
} from '@mui/material';
import PatientInfo from './PatientInfo';
import PharmacyDetails from './PharmacyDetails';
import InsuranceDetails from './InsuranceDetails';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ROUTE } from 'route/RouteEnums';
import GuardianInfo from './GuardianInfo/GuardianInfo';
import COLORS from 'theme/colors';
import { Page } from 'components/Page';
import { useAppDispatch } from 'store/hooks';
import { AuthContext } from 'utils/AuthContext';
import { getProfileStatus, getSibling } from 'store/slices/userSlice';
import { uploadProfileImageAPI, getProfileImageAPI } from 'services/profileServices';
import FileUploader from './InsuranceDetails/FileUploader';
import CounselorInfo from './CounslerInfo';
import { useSectionAutoScroll } from './useSectionAutoScroll';
import { getDefaultLandingPage } from 'utils/navigationUtils';
import { smallButtonStyle, StyledDialog } from 'theme/index';

const Profile = (props: any) => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const dispatch = useAppDispatch();

  const [selectedPanel, setSelectedPanel] = useState<any>();
  const [status, setStatus] = useState<any>();
  const [insuranceStatus, setInsuranceStatus] = useState<boolean>(false);
  const [profilePicture, setProfilePicture] = useState<any>(null);
  const [newSibling, setNewSibling] = useState('');
  const [siblings, setSiblings] = useState<any>([]);
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const addSiblings = query.get('addSiblings') === 'true';
  const { user, setUserDetails } = useContext(AuthContext);
  const isAdmin = user.isAdmin;
  const patientId = isAdmin ? props?.patientId || state?.patientId : user?.patientId;
  const isSiblingEnabled = !isAdmin && addSiblings;
  const [openDialog, setOpenDialog] = useState(false);
  const [blockedPatientId, setBlockedPatientId] = useState<string | null>(null);
  const [siblingsLoaded, setSiblingsLoaded] = useState(false);
  const [profileCheckLoader, setProfileCheckLoader] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');
  const MAX_SIBLINGS = 15;
  /**
   * MM-842: keeps each opened profile section fully in view (centred, or top-aligned under the app bar when
   * taller than the screen), so the user never has to scroll to find the section that just opened.
   */
  const { registerSection, scrollToSection } = useSectionAutoScroll();
  /**
   * MM-842: set once the section opened on arrival has been scrolled to. Sections opened afterwards on this
   * page are scrolled from the accordion's onEntered instead, which the section open on arrival never fires.
   */
  const initialPanelScrolled = useRef(false);
  /**
   * MM-850: the section the page opened by itself on a landing visit (the first incomplete one), until it has
   * been scrolled to. It is scrolled with its middle in the middle of the screen; sections the user opens keep
   * the MM-842 alignment.
   */
  const autoOpenedPanel = useRef<string | null>(null);
  /**
   * MM-850: true when a patient/guardian lands on the Profile page (Manage Profile menu, /profile, a
   * "complete your profile" link) rather than opening Patient's Information from the page itself. Such a
   * visit opens the first section still to be completed, or keeps every section collapsed when the profile
   * is complete, so all the sections are seen first. Add Child keeps Patient's Information open for the new child.
   */
  const opensFirstIncompletePanel =
    !isAdmin && !addSiblings && props?.for === 'patientInfo' && !state?.fromProfileSection;

  /**
   * MM-1123: only one patient profile is shown at a time (switching lives in the navigation bar), so the
   * sibling list is fetched only for the Add Child flow, which needs it for the 15-child limit and the
   * "complete the current profile first" check. It runs each time the flow starts, not only on mount:
   * the profile routes reuse this component, so starting Add Child from a profile page does not remount
   * it, and a mount-only fetch left the loader spinning. MM-850: for the same reason Patient's Information is
   * opened here, so the new child's details are open even when the sections were collapsed before.
   */
  useEffect(() => {
    if (isSiblingEnabled) {
      setSelectedPanel('patientInfo');
      setSiblingsLoaded(false);
      getSiblings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSiblingEnabled]);
  /**
   * MM-1123: runs the Add Child checks once the fresh sibling list has arrived (siblingsLoaded turns true),
   * never on a list left over from an earlier visit.
   */
  useEffect(() => {
    if (isSiblingEnabled && siblingsLoaded) {
      handleAddNewSibling();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siblingsLoaded]);

  const handleSiblingAdd = () => {
    if (isAdmin) {
      navigate(ROUTE.ADMIN_GUARDIAN_INFORMATION, {
        state: { patientId },
      });
    } else {
      navigate(ROUTE.GUARDIAN_INFORMATION);
    }
  };

  const getProfileCompletionStatus = () => {
    dispatch(getProfileStatus(patientId))
      .unwrap()
      .then((response: any) => {
        if (response?.status !== 200) {
          return;
        }
        const { data } = response;
        const status = {
          patientInfo: data?.patientInfoStatus,
          guardianInfo: data?.guardianInfoStatus,
          providerInfo: data?.childProviderInfoStatus,
          counselorInfo: data?.childCounselorInfoStatus,
          pharmacyDetails: data?.pharmacyInfoStatus,
          insuranceDetails: data?.paymentInfoStatus,
        };
        setStatus(status);
        if (opensFirstIncompletePanel) {
          const firstIncompletePanel = getFirstIncompletePanel(status);
          autoOpenedPanel.current = firstIncompletePanel;
          setSelectedPanel(firstIncompletePanel);
        }
        const isLastProfileCompleted =
          data?.patientInfoStatus &&
          data?.guardianInfoStatus &&
          data?.childProviderInfoStatus &&
          data?.childCounselorInfoStatus &&
          data?.pharmacyInfoStatus &&
          data?.paymentInfoStatus;

        if (isLastProfileCompleted) {
          const data = {
            ...user,
            profileStatus: 'complete',
          };
          setUserDetails(data);
        } else {
          const data = {
            ...user,
            profileStatus: 'incomplete',
          };
          setUserDetails(data);
        }
      });
  };

  /**
   * MM-850: returns the key of the first section, in page order, that is not complete yet, or null when every
   * section is complete.
   */
  const getFirstIncompletePanel = (profileStatus: Record<string, boolean>): string | null =>
    Object.keys(profileStatus).find((key) => !profileStatus[key]) ?? null;

  /**
   * Opens the section of the current route. MM-850: on a landing visit no section is open until the profile
   * status says which one, if any, still needs completing.
   */
  useEffect(() => {
    setSelectedPanel(opensFirstIncompletePanel ? null : props?.for);
    getImage();
    getProfileCompletionStatus();
    //eslint-disable-next-line
  }, [dispatch, props.for, patientId, insuranceStatus]);

  /**
   * MM-842: when the page is opened straight onto a later panel (e.g. Billing → Insurance, Dashboard or the
   * profile-completion prompt → /profile/insurance), the panel expands but sits below the collapsed panels
   * above it, off-screen on phones. Once the accordions have rendered (they wait for the profile status),
   * bring the opened panel into view. Runs only once per visit and skips the first panel, which is already
   * at the top, so the page title and photo stay visible on a plain /profile visit. MM-850: on a landing visit
   * the opened panel is the first incomplete one, set together with the status, and is scrolled to even when
   * it is the first panel.
   */
  useEffect(() => {
    if (!status || initialPanelScrolled.current) return;
    initialPanelScrolled.current = true;
    const openedPanel = opensFirstIncompletePanel ? selectedPanel : props?.for;
    if (!openedPanel || (openedPanel === 'patientInfo' && openedPanel !== autoOpenedPanel.current)) return;
    scrollToOpenedSection(openedPanel);
    //eslint-disable-next-line
  }, [status]);

  /**
   * MM-850: scrolls to a section that has just opened. The section the page opened by itself is placed with
   * its middle in the middle of the screen (once only); any other keeps the MM-842 alignment.
   */
  const scrollToOpenedSection = (key: string) => {
    const openedAutomatically = autoOpenedPanel.current === key;
    autoOpenedPanel.current = null;
    scrollToSection(key, openedAutomatically);
  };

  const getSiblings = () => {
    dispatch(getSibling({}))
      .unwrap()
      .then((response: any) => {
        if (response?.status === 200) {
          setSiblings(response.data);
        }
      })
      .finally(() => {
        setSiblingsLoaded(true);
      });
  };

  const navigateRoute = (path: string) => {
    if (isAdmin) navigate(path, { state: { patientId } });
    else navigate(path);
  };

  /**
   * MM-850: handles a click on a section header. An opening section is opened here as well as through its
   * route, because opening Patient's Information on a fully collapsed page keeps the same route, so the route
   * effect does not run again. fromProfileSection marks the visit as coming from this page, so it is not
   * treated as a landing visit that collapses the sections.
   */
  const handleSectionChange = (item: any, expanded: boolean) => {
    if (expanded) setSelectedPanel(item.key);
    const path = isAdmin ? item.adminRoute : item.route;
    navigate(path, { state: isAdmin ? { patientId, fromProfileSection: true } : { fromProfileSection: true } });
  };

  const getImage = () => {
    getProfileImageAPI(patientId).then((response: any) => {
      if (response.status === 200 && response.data) {
        setProfilePicture(response.data);
      } else {
        setProfilePicture('');
      }
    });
  };

  const handleProfileImage = (acceptedFiles: any) => {
    if (acceptedFiles?.length > 0) {
      const file = acceptedFiles[0];
      const reader = new FileReader();
      reader.onload = () => {
        setProfilePicture(file);
      };
      reader.readAsDataURL(file);
      const formData: any = new FormData();
      formData.append('file', file);
      uploadProfileImageAPI(formData, patientId);
    }
    getImage();
  };

  const handleAddNewSibling = async () => {
    if (!siblingsLoaded) return;

    // ✅ Limit check
    if (siblings?.length >= MAX_SIBLINGS) {
      setDialogMessage('You can only add up to 15 siblings.');
      setOpenDialog(true);
      return;
    }
    setProfileCheckLoader(true);
    // ✅ Include current + siblings
    const patientList = Array.from(new Set([user?.patientId, ...(siblings?.map((s: any) => s.id) ?? [])]));

    for (let i = 0; i < patientList.length; i++) {
      const currentPatientId = patientList[i];
      if (!currentPatientId) continue;

      let response: any;
      try {
        response = await dispatch(getProfileStatus(currentPatientId)).unwrap();
      } catch (err) {
        console.error('Profile status check failed', err);
        // MM-1123: a failed status check must not leave the Add Child loader spinning.
        setProfileCheckLoader(false);
        return;
      }
      const data = response?.data;

      const isIncomplete =
        !data?.patientInfoStatus ||
        !data?.guardianInfoStatus ||
        !data?.childProviderInfoStatus ||
        !data?.childCounselorInfoStatus ||
        !data?.pharmacyInfoStatus ||
        !data?.paymentInfoStatus;

      if (isIncomplete) {
        setDialogMessage('Please complete the profile of the current sibling before adding a new one.');
        setBlockedPatientId(currentPatientId);

        setStatus({
          patientInfo: data?.patientInfoStatus,
          guardianInfo: data?.guardianInfoStatus,
          providerInfo: data?.childProviderInfoStatus,
          counselorInfo: data?.childCounselorInfoStatus,
          pharmacyDetails: data?.pharmacyInfoStatus,
          insuranceDetails: data?.paymentInfoStatus,
        });

        setOpenDialog(true);
        setProfileCheckLoader(false);
        return; // ✅ stop at first incomplete
      }
    }
    setProfileCheckLoader(false);
    // ✅ All complete → allow flow
    setNewSibling('new');
    navigate(`${ROUTE.PATIENT_INFORMATION}?addSiblings=true`);
  };
  const handleCounselorComplete = () => {
    navigateRoute(isAdmin ? ROUTE.ADMIN_PHARMACY_DETAILS : ROUTE.PHARMACY_DETAILS);
  };

  const handleProviderComplete = () => {
    navigateRoute(isAdmin ? ROUTE.ADMIN_COUNSELOR_INFORMATION : ROUTE.COUNSELOR_INFORMATION);
  };

  const handlePharmacyComplete = () => {
    navigateRoute(isAdmin ? ROUTE.ADMIN_INSURANCE_DETAILS : ROUTE.INSURANCE_DETAILS);
  };

  const handleGuardianComplete = () => {
    navigateRoute(isAdmin ? ROUTE.ADMIN_PROVIDER_INFORMATION : ROUTE.PROVIDER_INFORMATION);
  };

  const handleDialogOk = () => {
    setOpenDialog(false);
    if (dialogMessage?.includes('only add up to')) {
      return; // ❌ do nothing
    }
    // ✅ ensure correct patient context
    if (blockedPatientId) {
      setUserDetails({
        ...user,
        patientId: blockedPatientId,
      });
    }
    // ✅ navigate to first incomplete step
    if (!status?.patientInfo) navigateRoute(ROUTE.PATIENT_INFORMATION);
    else if (!status?.guardianInfo) navigateRoute(ROUTE.GUARDIAN_INFORMATION);
    else if (!status?.providerInfo) navigateRoute(ROUTE.PROVIDER_INFORMATION);
    else if (!status?.counselorInfo) navigateRoute(ROUTE.COUNSELOR_INFORMATION);
    else if (!status?.pharmacyDetails) navigateRoute(ROUTE.PHARMACY_DETAILS);
    else navigateRoute(ROUTE.INSURANCE_DETAILS);
  };
  const getPanelDisabled = (statusKey: string): boolean => {
    if (isAdmin) return false;
    if (isSiblingEnabled) return true;
    return !status?.[statusKey];
  };

  const getStatusIcon = (key: string) => {
    if (selectedPanel === key) return <VerifiedIcon sx={{ color: COLORS.White }} />;
    if (status?.[key]) return <VerifiedIcon sx={{ color: COLORS.Link }} />;
    return <VerifiedIcon sx={{ color: COLORS.Gray300 }} />;
  };

  const AccordionData = [
    {
      id: 1,
      summary: 'Patient’s Information',
      key: 'patientInfo',
      route: ROUTE.PATIENT_INFORMATION,
      adminRoute: ROUTE.ADMIN_PATIENT_INFORMATION,
      component: (
        <PatientInfo onComplete={() => handleSiblingAdd()} from={isAdmin ? props?.from : null} patientId={patientId} />
      ),
    },
    {
      id: 2,
      summary: 'Guardian Information',
      key: 'guardianInfo',
      route: ROUTE.GUARDIAN_INFORMATION,
      adminRoute: ROUTE.ADMIN_GUARDIAN_INFORMATION,
      disabled: getPanelDisabled('patientInfo'),
      component: (
        <GuardianInfo onComplete={handleGuardianComplete} from={isAdmin ? props?.from : null} patientId={patientId} />
      ),
    },
    {
      id: 3,
      //eslint-disable-next-line
      summary: "Child's Primary Care Provider's Information ",
      key: 'providerInfo',
      route: ROUTE.PROVIDER_INFORMATION,
      adminRoute: ROUTE.ADMIN_PROVIDER_INFORMATION,
      disabled: getPanelDisabled('guardianInfo'),
      component: (
        <ProviderDetails
          onComplete={handleProviderComplete}
          from={isAdmin ? props?.from : null}
          patientId={patientId}
          newSibling={newSibling}
        />
      ),
    },
    {
      id: 4,
      //eslint-disable-next-line
      summary: "Child's Counselor or Therapist Information ",
      key: 'counselorInfo',
      route: ROUTE.COUNSELOR_INFORMATION,
      adminRoute: ROUTE.ADMIN_COUNSELOR_INFORMATION,
      disabled: getPanelDisabled('providerInfo'),
      component: (
        <CounselorInfo
          onComplete={handleCounselorComplete}
          from={isAdmin ? props?.from : null}
          patientId={patientId}
          newSibling={newSibling}
        />
      ),
    },
    {
      id: 5,
      summary: 'Pharmacy Details',
      key: 'pharmacyDetails',
      route: ROUTE.PHARMACY_DETAILS,
      adminRoute: ROUTE.ADMIN_PHARMACY_DETAILS,
      disabled: getPanelDisabled('counselorInfo'),
      component: (
        <PharmacyDetails
          onComplete={handlePharmacyComplete}
          from={isAdmin ? props?.from : null}
          patientId={patientId}
        />
      ),
    },
    {
      id: 6,
      summary: 'Insurance Information',
      key: 'insuranceDetails',
      route: ROUTE.INSURANCE_DETAILS,
      adminRoute: ROUTE.ADMIN_INSURANCE_DETAILS,
      disabled: getPanelDisabled('pharmacyDetails'),
      component: (
        <InsuranceDetails
          onComplete={() => {
            setSelectedPanel(null);
            setInsuranceStatus(!insuranceStatus);
          }}
          from={isAdmin ? props?.from : null}
          patientId={patientId}
          launchFromPatientSummery={false}
        />
      ),
    },
  ];

  return (
    <Page
      {...(isAdmin && {
        backDir: `${ROUTE.ADMIN_PATIENT_INFO}/${patientId}`,
        state: patientId,
      })}
    >
      <>
        <Backdrop
          sx={{
            color: '#fff',
            zIndex: (theme) => theme.zIndex.drawer + 1,
          }}
          open={isSiblingEnabled && (!siblingsLoaded || profileCheckLoader)}
        >
          <CircularProgress color='inherit' />
        </Backdrop>
        <Container component='main' maxWidth='md'>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Grid container>
              <Grid item xs={10} display={'flex'} alignItems={'center'}>
                <Typography variant='h1' gutterBottom>
                  {/* MM-1132: "Emma’s Profile" for the open patient, or "Child’s Profile" when no name is set
                      or a new sibling is being added. Admin view has no sibling list, so it keeps
                      "Profile Information". */}
                  {isAdmin && 'Profile Information'}
                  {isSiblingEnabled && 'Child’s Profile'}
                  {!isAdmin && !addSiblings && <ChildNameHeading title='Profile' fallbackTitle='Child’s Profile' />}
                </Typography>
              </Grid>
              <Grid item xs={2}>
                {profilePicture !== null ? (
                  <IconButton>
                    <FileUploader
                      onFileUpload={handleProfileImage}
                      profileImage={true}
                      uploadProgress={0}
                      profilePicture={profilePicture}
                      disable={isAdmin}
                    />
                  </IconButton>
                ) : null}
              </Grid>
            </Grid>
            {status &&
              AccordionData.map((item: any, index: number) => {
                return (
                  <Accordion
                    key={item.key}
                    ref={registerSection(item.key)}
                    // MM-850: null means every section is collapsed; only an unset panel falls back to the first.
                    expanded={selectedPanel === undefined ? index === 0 : selectedPanel === item.key}
                    disabled={item.disabled}
                    onChange={(_event, expanded) => handleSectionChange(item, expanded)}
                    // MM-842: once a section finishes opening on this page, bring it fully into view.
                    TransitionProps={{ onEntered: () => scrollToOpenedSection(item.key) }}
                    sx={{ width: '100%', borderRadius: '8px' }}
                  >
                    <AccordionSummary
                      expandIcon={
                        selectedPanel === item.key ? (
                          <RemoveIcon sx={{ color: '#797979' }} onClick={() => setSelectedPanel(index)} />
                        ) : (
                          <AddIcon sx={{ color: '#797979' }} onClick={() => setSelectedPanel(item.key)} />
                        )
                      }
                      aria-controls='panel-content'
                      id='panel-header'
                      sx={{ flexDirection: 'row-reverse' }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          flex: 1,
                          // flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Typography variant='h4' color={COLORS.FormInput} sx={{ lineHeight: 1.4 }}>
                          &nbsp;{item.summary}
                        </Typography>
                        {getStatusIcon(item.key)}
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>{selectedPanel === item.key ? item.component : null}</AccordionDetails>
                  </Accordion>
                );
              })}

            <Button sx={{ mt: '20px' }} onClick={() => navigate(getDefaultLandingPage())}>
              Go to Dashboard
            </Button>
          </Box>
        </Container>
        <StyledDialog
          open={openDialog}
          disableEscapeKeyDown
          maxWidth='xs' // ✅ smaller width
          fullWidth
        >
          <DialogTitle sx={{ textAlign: 'center', pb: 1, fontSize: '1rem', fontWeight: 600 }}>
            Complete Your Profile
          </DialogTitle>

          <DialogContent sx={{ textAlign: 'center', p: 2 }}>
            <Typography sx={{ fontSize: '13px' }}>{dialogMessage}</Typography>
          </DialogContent>

          <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
            <Button
              variant='contained'
              size='small'
              onClick={handleDialogOk}
              sx={{
                minWidth: 90,
                px: 1.5,
                py: 0.5,
                fontSize: '0.75rem',
                borderRadius: '16px',
                textTransform: 'none',
              }}
            >
              OK
            </Button>
          </DialogActions>
        </StyledDialog>
      </>
    </Page>
  );
};

export default Profile;
